import React, { useEffect, useState } from 'react';
import { CheckCircle, Gift, Users, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui';
import PaymentService from '../services/paymentService';

const PaymentSuccessPage: React.FC = () => {
  const [paymentStatus, setPaymentStatus] = useState<{
    loading: boolean;
    verified: boolean;
    paymentId: string | null;
    featureType: string | null;
    amount?: number | undefined;
    status?: string | undefined;
    statusMessage?: string | undefined;
    originUrl?: string | null | undefined;
  }>({
    loading: true,
    verified: false,
    paymentId: null,
    featureType: null
  });

  const [modalMode, setModalMode] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  useEffect(() => {
    const verifyPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentId = urlParams.get('payment');
      const featureType = urlParams.get('feature');
      const isModalMode = urlParams.get('modal') === 'true';
      setModalMode(isModalMode);

      if (paymentId) {
        try {
          const status = await PaymentService.getPaymentStatus(paymentId);
          
          // Map Mollie statuses to user-friendly messages
          const getStatusMessage = (mollieStatus: string) => {
            switch (mollieStatus) {
              case 'paid': return 'Betaling succesvol voltooid';
              case 'open': return 'Betaling nog niet voltooid';
              case 'pending': return 'Betaling wordt verwerkt';
              case 'failed': return 'Betaling mislukt';
              case 'canceled': return 'Betaling geannuleerd';
              case 'cancelled': return 'Betaling geannuleerd';
              case 'expired': return 'Betaling verlopen';
              default: return `Betaling status: ${mollieStatus}`;
            }
          };

          // Since apiGet extracts data automatically, status is the payment data directly
          const paymentData = {
            loading: false,
            verified: status?.isPaid || false,
            paymentId,
            featureType: featureType || status?.metadata?.featureType || null,
            amount: status ? parseFloat(status.amount.value) : undefined,
            status: status?.status,
            statusMessage: status ? getStatusMessage(status.status) : 'Onbekende status',
            originUrl: status?.metadata?.originUrl || null
          };

          setPaymentStatus(paymentData);

          // Auto-redirect for successful payments if origin URL is available
          if (paymentData.verified && paymentData.originUrl && isModalMode) {
            const originUrl = paymentData.originUrl; // narrowed to string for the async closure
            const redirectCountdownInterval = setInterval(() => {
              setRedirectCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(redirectCountdownInterval);
                  const redirectUrl = new URL(originUrl, window.location.origin);
                  redirectUrl.searchParams.set('payment_success', 'true');
                  redirectUrl.searchParams.set('feature', paymentData.featureType || '');
                  redirectUrl.searchParams.set('amount', paymentData.amount?.toString() || '');
                  window.location.href = redirectUrl.toString();
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          }
        } catch (error) {
          console.error('Error verifying premium payment:', error);
          setPaymentStatus({
            loading: false,
            verified: false,
            paymentId,
            featureType,
            status: 'error',
            statusMessage: 'Kon betalingsstatus niet ophalen'
          });
        }
      } else {
        setPaymentStatus({
          loading: false,
          verified: false,
          paymentId: null,
          featureType,
          status: 'no_payment_id',
          statusMessage: 'Geen betalings-ID gevonden'
        });
      }
    };

    verifyPayment();
  }, []);

  const getFeatureIcon = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return <Users className="w-16 h-16 text-neon-cyan" />;
      case 'EXTENDED_GROUP_SIZE':
        return <Users className="w-16 h-16 text-neon-cyan" />;
      case 'CHAT_ENHANCEMENTS':
        return <MessageCircle className="w-16 h-16 text-neon-cyan" />;
      default:
        return <Gift className="w-16 h-16 text-neon-cyan" />;
    }
  };

  const getFeatureTitle = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Extra Groep Plekken';
      case 'EXTENDED_GROUP_SIZE':
        return 'Groep Uitbreiding';
      case 'CHAT_ENHANCEMENTS':
        return 'Chat Verbeteringen';
      default:
        return 'Premium Feature';
    }
  };

  const getFeatureDescription = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Je kunt nu in meer groepen tegelijkertijd! Ga naar je groepen om je nieuwe limiet te zien.';
      case 'EXTENDED_GROUP_SIZE':
        return 'Je groep kan nu meer leden hebben! De nieuwe capaciteit is direct actief.';
      case 'CHAT_ENHANCEMENTS':
        return 'Je kunt nu GIFs sturen in je groep chats! Maak je gesprekken nog leuker.';
      default:
        return 'Je premium feature is succesvol geactiveerd!';
    }
  };

  const getSmartRedirectPath = (featureType: string, originUrl?: string) => {
    // If we have origin URL from payment metadata, use that
    if (originUrl && originUrl !== '') {
      return originUrl;
    }
    
    // Fallback to feature-specific paths
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return '/groups';
      case 'EXTENDED_GROUP_SIZE':
        return '/groups';
      case 'CHAT_ENHANCEMENTS':
        return '/groups';
      default:
        return '/dashboard';
    }
  };

  const getReturnButtonText = (featureType: string, originUrl?: string) => {
    if (originUrl && originUrl !== '') {
      if (originUrl.includes('/groups')) return 'Terug naar Groepen';
      if (originUrl.includes('/premium')) return 'Terug naar Premium';
      if (originUrl.includes('/dashboard')) return 'Terug naar Dashboard';
      return 'Terug waar je was';
    }

    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
      case 'EXTENDED_GROUP_SIZE':
        return 'Ga naar Mijn Groepen';
      case 'CHAT_ENHANCEMENTS':
        return 'Ga naar Groep Chat';
      default:
        return 'Ga naar Dashboard';
    }
  };

  if (paymentStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-neon-cyan mx-auto mb-4"></div>
          <p className="text-lg text-fg-muted">iDEAL betalingsstatus controleren...</p>
        </div>
      </div>
    );
  }

  // Compact redirect view for modal mode
  if (modalMode && paymentStatus.verified && paymentStatus.originUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div
            className="w-20 h-20 mx-auto mb-4 rounded-full bg-neon-lime/[.12] flex items-center justify-center"
            style={{ boxShadow: '0 0 40px rgba(184,255,61,.35)' }}
          >
            <CheckCircle className="w-10 h-10 text-neon-lime" />
          </div>
          <h2 className="text-2xl font-bold text-fg-bright mb-2">
            Betaling Succesvol! 🎉
          </h2>
          <p className="text-fg-muted mb-4">
            Je wordt automatisch teruggebracht naar waar je was...
          </p>
          <p className="text-lg font-medium text-neon-cyan font-mono">
            {redirectCountdown} seconden
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-2.5">
          <span className="sf-logo-mark h-[34px] w-[34px] text-[19px]">S</span>
          <span className="text-xl font-extrabold tracking-tight text-fg-bright">SOLOFIX</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 pb-16">
        <div className="sf-card overflow-hidden">
          {paymentStatus.verified ? (
            <>
              {/* Success Content */}
              <div className="px-8 pt-12 pb-8 text-center">
                <div
                  className="w-24 h-24 mx-auto mb-6 rounded-full bg-neon-lime/[.12] flex items-center justify-center"
                  style={{ boxShadow: '0 0 40px rgba(184,255,61,.35)' }}
                >
                  <CheckCircle className="w-12 h-12 text-neon-lime" />
                </div>
                <div className="sf-eyebrow mb-3">// let's go</div>
                <h2 className="text-3xl font-bold text-fg-bright tracking-tight mb-4">
                  Betaling Succesvol! 🎉
                </h2>
                <p className="text-lg text-fg-muted">
                  Je premium feature is geactiveerd en direct beschikbaar
                </p>
              </div>

              <div className="px-8 py-8 text-center">
                {paymentStatus.featureType && (
                  <div className="mb-8">
                    <div className="flex justify-center mb-4">
                      {getFeatureIcon(paymentStatus.featureType)}
                    </div>
                    <h3 className="text-2xl font-bold text-fg-bright mb-2">
                      {getFeatureTitle(paymentStatus.featureType)}
                    </h3>
                    <p className="text-lg text-fg-body mb-6">
                      {getFeatureDescription(paymentStatus.featureType)}
                    </p>
                  </div>
                )}

                {paymentStatus.amount && (
                  <div className="sf-card-highlight p-6 mb-8">
                    <p className="sf-label-mono mb-1">Betaald bedrag</p>
                    <p className="text-4xl font-bold text-fg-bright">
                      €{paymentStatus.amount.toFixed(2)}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <Button
                    onClick={() => window.location.href = getSmartRedirectPath(paymentStatus.featureType || '', paymentStatus.originUrl || undefined)}
                    className="w-full py-3 text-lg"
                  >
                    {getReturnButtonText(paymentStatus.featureType || '', paymentStatus.originUrl || undefined)}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>

                  <button
                    onClick={() => window.location.href = '/dashboard'}
                    className="sf-link"
                  >
                    Ga naar Dashboard
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* FAILED/OTHER STATUS */
            <>
              <div className="px-8 pt-12 pb-8 text-center">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-full border flex items-center justify-center ${
                  paymentStatus.status === 'open' || paymentStatus.status === 'pending'
                    ? 'bg-warning-500/10 border-warning-500/30'
                    : 'bg-neon-pink/[.08] border-neon-pink/30'
                }`}>
                  <span className="text-4xl">
                    {paymentStatus.status === 'open' || paymentStatus.status === 'pending' ? '⏳' : '⚠️'}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-fg-bright tracking-tight mb-4">
                  {paymentStatus.status === 'open' ? 'Betaling Open' :
                   paymentStatus.status === 'pending' ? 'Betaling Verwerken' :
                   paymentStatus.status === 'canceled' || paymentStatus.status === 'cancelled' ? 'Betaling Geannuleerd' :
                   paymentStatus.status === 'expired' ? 'Betaling Verlopen' :
                   'Premium Upgrade Mislukt'}
                </h2>
                <p className={`text-lg ${
                  paymentStatus.status === 'open' || paymentStatus.status === 'pending'
                    ? 'text-warning-400'
                    : 'text-neon-pink-soft'
                }`}>
                  {paymentStatus.statusMessage || 'Er is iets mis gegaan met je premium upgrade'}
                </p>
              </div>

              <div className="px-8 py-8 text-center">
                {paymentStatus.status === 'open' || paymentStatus.status === 'pending' ? (
                  <>
                    <p className="text-fg-body mb-6">
                      Je betaling is nog niet voltooid. Dit kan enkele minuten duren.
                      Je kunt deze pagina vernieuwen om de status te controleren.
                    </p>
                    <div className="space-y-4">
                      <Button
                        onClick={() => window.location.reload()}
                        className="w-full py-3 text-lg"
                      >
                        Status Vernieuwen
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-fg-body mb-6">
                      {paymentStatus.status === 'canceled' || paymentStatus.status === 'cancelled' ?
                        'Je hebt de betaling geannuleerd. Je premium feature is nog niet geactiveerd.' :
                        paymentStatus.status === 'expired' ?
                        'De betaling is verlopen. Je premium feature is nog niet geactiveerd.' :
                        'Je premium upgrade is niet voltooid. Er is geen bedrag van je rekening afgeschreven.'
                      }
                    </p>

                    {paymentStatus.paymentId && (
                      <div className="sf-item p-4 mb-6">
                        <p className="sf-label-mono mb-2">Betalings-ID:</p>
                        <p className="font-mono text-xs text-fg-soft">{paymentStatus.paymentId}</p>
                        <p className="text-sm text-fg-muted mt-2">Status: {paymentStatus.statusMessage}</p>
                      </div>
                    )}

                    <div className="space-y-4">
                      <Button
                        onClick={() => {
                          // Try to go back to the origin URL, fallback to dashboard
                          const fallbackUrl = paymentStatus.originUrl || '/dashboard';
                          window.location.href = fallbackUrl;
                        }}
                        className="w-full py-3 text-lg"
                      >
                        Probeer Opnieuw
                      </Button>

                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;